import { useRef } from "react";
import { motion } from "framer-motion";
import {
  UsersRound, ScanLine, BellRing, MessageCircle, Search, Download,
  Cloud, FileDown, Trash2, Check,
} from "lucide-react";
import { MockPhoneFrame, MockGroup, MockFollowups, MockSearch, MockScanner, MockContact } from "./MockPhone";

const features = [
  {
    icon: UsersRound, title: "Smart Groups & sub-groups",
    desc: "Distributors from wholesalers from VIP clients — one tap. Assign a contact to multiple groups, colour-code them, and never mix them up again.",
    span: "md:col-span-8", accent: true, screen: "group", align: "right",
  },
  {
    icon: ScanLine, title: "AI Business Card Scanner",
    desc: "Snap a card. Name, company, phone, and email arrive as a saved contact in about two seconds.",
    span: "md:col-span-4", screen: "scanner",
  },
  {
    icon: BellRing, title: "Follow-up Dashboard",
    desc: "Today. Upcoming. Overdue. Done. Every reminder lives in one place — with snooze, reschedule, and complete built in.",
    span: "md:col-span-4", screen: "followups",
  },
  {
    icon: MessageCircle, title: "One-tap Call &amp; WhatsApp",
    desc: "Call or WhatsApp any contact straight from their profile — every call automatically logged for you.",
    span: "md:col-span-4", screen: "contact",
  },
  {
    icon: Search, title: "Search that reads your notes",
    desc: "Find anyone by name, company, phone, email, or the note you wrote three months ago. Duplicate detection built in.",
    span: "md:col-span-4", screen: "search",
  },
  {
    icon: Download, title: "You own your data",
    desc: "Cloud sync across devices, plus one-click CSV export whenever you want. Delete your account any time — your network belongs to you.",
    span: "md:col-span-12", custom: "ownership",
  },
];

function GlowCard({ children, className = "", testid }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r || !ref.current) return;
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <div ref={ref} onMouseMove={onMove} className={`card-elev ${className}`} data-testid={testid}>
      {children}
    </div>
  );
}

const SCREEN_MAP = {
  group: MockGroup,
  followups: MockFollowups,
  search: MockSearch,
  scanner: MockScanner,
  contact: MockContact,
};

export default function Features() {
  return (
    <section id="features" className="relative py-24 md:py-40 border-t border-black/5" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div>
            <div className="overline mb-4">What&apos;s inside</div>
            <h2 className="font-display text-4xl md:text-6xl font-medium leading-[0.95] tracking-tight max-w-2xl text-[color:var(--ink)]">
              Six reasons your phonebook is
              <span
                className="gradient-text"
                style={{ fontFamily: "var(--font-serif)", fontStyle: "italic" }}
              >
                {" "}
                about to feel obsolete.
              </span>
            </h2>
          </div>
          <p className="text-[color:var(--ink-soft)] max-w-sm text-base md:text-lg">
            Every feature is built around one job — helping you remember the right person at the right moment.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-4 md:gap-5">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
              className={f.span}
            >
              <GlowCard className="h-full p-8 md:p-10" testid={`feature-card-${i}`}>
                {f.custom === "ownership" ? (
                  <OwnershipContent {...f} index={i} />
                ) : (
                  <FeatureContent {...f} index={i} />
                )}
              </GlowCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureContent({ icon: Icon, title, desc, accent, index, screen, align }) {
  const ScreenComp = screen ? SCREEN_MAP[screen] : null;
  const hasScreen = !!ScreenComp;
  return (
    <div className="relative h-full flex flex-col justify-between min-h-[280px]">
      <div className={hasScreen && align === "right" ? "flex gap-8 items-start" : ""}>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: accent
                  ? "linear-gradient(135deg, var(--accent-soft), var(--blue-soft))"
                  : "var(--surface-2)",
                border: "1px solid var(--line)",
              }}
            >
              <Icon className="w-4 h-4 text-[color:var(--ink)]" strokeWidth={1.6} />
            </div>
            <span className="text-[color:var(--ink-mute)] text-xs font-mono">0{index + 1}</span>
          </div>
          <h3
            className="font-display text-2xl md:text-3xl font-medium leading-[1.15] text-[color:var(--ink)] mt-8 max-w-md"
            dangerouslySetInnerHTML={{ __html: title }}
          />
          <p className="text-[color:var(--ink-soft)] text-sm md:text-base leading-relaxed max-w-md mt-6">{desc}</p>
        </div>

        {hasScreen && align === "right" && (
          <div className="hidden md:block w-[190px] shrink-0 self-end -mb-12 -mr-4">
            <MockPhoneFrame>
              <ScreenComp />
            </MockPhoneFrame>
          </div>
        )}
      </div>

      {hasScreen && align !== "right" && (
        <div className="mt-6 relative rounded-2xl overflow-hidden border border-[color:var(--line)] bg-gradient-to-b from-[color:var(--surface-2)] to-white flex justify-center pt-4 pb-4">
          <div className="w-[170px]">
            <MockPhoneFrame>
              <ScreenComp />
            </MockPhoneFrame>
          </div>
        </div>
      )}

      {accent && (
        <div
          className="absolute -top-4 -right-6 w-40 h-40 rounded-full opacity-40 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, var(--accent), transparent 70%)" }}
        />
      )}
    </div>
  );
}

function OwnershipContent({ icon: Icon, title, desc, index }) {
  return (
    <div className="relative grid md:grid-cols-12 gap-8 md:gap-10 items-center min-h-[280px]">
      <div className="md:col-span-6">
        <div className="flex items-center justify-between">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[color:var(--surface-2)] border border-[color:var(--line)]">
            <Icon className="w-4 h-4 text-[color:var(--ink)]" strokeWidth={1.6} />
          </div>
          <span className="text-[color:var(--ink-mute)] text-xs font-mono">0{index + 1}</span>
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-medium leading-[1.15] text-[color:var(--ink)] mt-8 max-w-md">
          {title}
        </h3>
        <p className="text-[color:var(--ink-soft)] text-sm md:text-base leading-relaxed max-w-md mt-4">{desc}</p>
      </div>

      {/* Ownership illustration */}
      <div className="md:col-span-6 relative">
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          {/* Cloud sync tile */}
          <div className="rounded-2xl border border-[color:var(--line)] bg-white p-4 md:p-5 flex flex-col justify-between min-h-[160px]">
            <div className="w-9 h-9 rounded-xl bg-[color:var(--blue-soft)] flex items-center justify-center">
              <Cloud className="w-4 h-4 text-[color:var(--blue)]" strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-[color:var(--ink)] text-sm font-medium">Cloud sync</div>
              <div className="text-[color:var(--ink-mute)] text-xs mt-1">All your devices, always fresh.</div>
            </div>
          </div>
          {/* CSV export tile */}
          <div className="rounded-2xl border border-[color:var(--accent)]/40 bg-gradient-to-b from-[color:var(--accent-soft)] to-white p-4 md:p-5 flex flex-col justify-between min-h-[160px] relative overflow-hidden">
            <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center">
              <FileDown className="w-4 h-4 text-[color:var(--accent)]" strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-[color:var(--ink)] text-sm font-medium">CSV export</div>
              <div className="text-[color:var(--ink-mute)] text-xs mt-1">1,247 contacts · one tap</div>
              <div className="mt-2 flex items-center gap-1 text-[10px] text-emerald-600">
                <Check className="w-3 h-3" /> Downloaded
              </div>
            </div>
          </div>
          {/* Delete account tile */}
          <div className="rounded-2xl border border-[color:var(--line)] bg-white p-4 md:p-5 flex flex-col justify-between min-h-[160px]">
            <div className="w-9 h-9 rounded-xl bg-[color:var(--purple-soft)] flex items-center justify-center">
              <Trash2 className="w-4 h-4 text-[color:var(--purple)]" strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-[color:var(--ink)] text-sm font-medium">Delete anytime</div>
              <div className="text-[color:var(--ink-mute)] text-xs mt-1">Wipe your data in one tap.</div>
            </div>
          </div>
        </div>
        {/* Connecting flow dots */}
        <svg className="absolute -bottom-2 left-0 right-0 mx-auto pointer-events-none" width="80%" height="24" viewBox="0 0 400 24" fill="none">
          <path d="M 30 12 L 200 12 L 370 12" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" className="text-[color:var(--line-strong)]" />
          <circle cx="30"  cy="12" r="3" fill="var(--blue)" />
          <circle cx="200" cy="12" r="3" fill="var(--accent)" />
          <circle cx="370" cy="12" r="3" fill="var(--purple)" />
        </svg>
      </div>
    </div>
  );
}
